import React, { useEffect, useRef, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { IInternalTicketServiceOption } from '../../../interfaces/newStudent/ticket.interface';
import * as S from './styles';

interface Props {
  label?: string;
  groups: IInternalTicketServiceOption[];
  selectedValues: number[];
  onChange: (values: number[]) => void;
  disabled?: boolean;
}

const extractAllIds = (options: IInternalTicketServiceOption[]): number[] =>
  options.flatMap((option) => [
    Number(option.value),
    ...(option.options?.length ? extractAllIds(option.options) : []),
  ]);

const findOptionTitle = (
  items: IInternalTicketServiceOption[],
  id: number,
): string | null => {
  for (const item of items) {
    if (Number(item.value) === id) {
      return item.title;
    }

    if (item.options?.length) {
      const nested = findOptionTitle(item.options, id);
      if (nested) {
        return nested;
      }
    }
  }

  return null;
};

export default function TicketServiceSelect({
  label,
  groups,
  selectedValues,
  onChange,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleExpandGroup = (groupId: string | number) => {
    setExpandedGroups((previous) => {
      const key = groupId.toString();
      const isExpanded = previous[key] ?? false;
      const nextState = { ...previous, [key]: !isExpanded };

      if (isExpanded) {
        const group = groups.find(
          (item) => Number(item.value) === Number(groupId),
        );
        if (group?.options?.length) {
          extractAllIds(group.options).forEach((id) => {
            nextState[id.toString()] = false;
          });
        }
      }

      return nextState;
    });
  };

  const toggleOption = (id: number) => {
    const currentValues = selectedValues ?? [];
    const newSelected = currentValues.includes(id) ? [] : [id];
    onChange(newSelected);
    setOpen(false);
  };

  const renderSelectedLabel = () => {
    if (!selectedValues.length) {
      return '[ SELECIONE ]';
    }

    return findOptionTitle(groups, selectedValues[0]) ?? '[ SELECIONE ]';
  };

  const renderOptions = (
    options: IInternalTicketServiceOption[],
    nested = false,
  ) =>
    options.map((option) => {
      const optionId = Number(option.value);
      const hasChildren = (option.options?.length ?? 0) > 0;
      const isExpanded = expandedGroups[optionId.toString()] ?? false;

      return (
        <React.Fragment key={optionId}>
          <S.DivInput
            $isChild={nested}
            onClick={(event) => {
              event.stopPropagation();
              if (hasChildren) {
                toggleExpandGroup(optionId);
                return;
              }

              toggleOption(optionId);
            }}
          >
            <S.CheckboxInput
              type="checkbox"
              readOnly
              checked={selectedValues.includes(optionId)}
            />
            <S.OptionTitle $isBold={hasChildren}>{option.title}</S.OptionTitle>
            {hasChildren && (
              <S.DownIcon>
                <KeyboardArrowDownIcon
                  fontSize="small"
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                />
              </S.DownIcon>
            )}
          </S.DivInput>
          {hasChildren && isExpanded && (
            <S.DivGroupNested>
              {renderOptions(option.options ?? [], true)}
            </S.DivGroupNested>
          )}
        </React.Fragment>
      );
    });

  return (
    <S.Container ref={containerRef}>
      {label && <S.Label>{label}</S.Label>}
      <S.DivSelect
        $disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((current) => !current);
          }
        }}
      >
        <S.SelectedLabel>{renderSelectedLabel()}</S.SelectedLabel>
        <S.DownIcon>
          <KeyboardArrowDownIcon fontSize="small" />
        </S.DownIcon>
        {open && !disabled && (
          <S.DivGeneral onClick={(event) => event.stopPropagation()}>
            {groups.map((group) => {
              const groupId = Number(group.value);
              const hasChildren = (group.options?.length ?? 0) > 0;
              const isExpanded = expandedGroups[groupId.toString()] ?? false;
              const isSelected = hasChildren
                ? group.options?.every((option) =>
                    selectedValues.includes(Number(option.value)),
                  )
                : selectedValues.includes(groupId);

              return (
                <S.DivGroup key={groupId}>
                  <S.DivInput
                    onClick={(event) => {
                      event.stopPropagation();
                      if (hasChildren) {
                        toggleExpandGroup(groupId);
                        return;
                      }

                      toggleOption(groupId);
                    }}
                  >
                    <S.CheckboxInput
                      type="checkbox"
                      readOnly
                      checked={Boolean(isSelected)}
                    />
                    <S.OptionTitle $isBold={hasChildren}>
                      {group.title}
                    </S.OptionTitle>
                    {hasChildren && (
                      <S.DownIcon>
                        <KeyboardArrowDownIcon
                          fontSize="small"
                          style={{
                            transform: isExpanded
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </S.DownIcon>
                    )}
                  </S.DivInput>
                  {hasChildren && isExpanded && (
                    <S.DivGroupNested>
                      {renderOptions(group.options ?? [], true)}
                    </S.DivGroupNested>
                  )}
                </S.DivGroup>
              );
            })}
          </S.DivGeneral>
        )}
      </S.DivSelect>
    </S.Container>
  );
}
